import { Logger } from 'wdc-cube';
import { formatDate } from '../../domain';
import { rememberPeriod } from './period-memory';
import { PeriodOptionScope, PeriodScope } from './period.scope';
import { clampRange, PERIOD_LABELS, PERIOD_SHORT_LABELS, periodRange } from './period.types';
const LOG = Logger.get('Mormaii.PeriodSelectorPresenter');
/**
 * O seletor de periodo - um presenter SEM place, hospedado por quem escolhe periodo.
 *
 * Mesmo padrao do `CompanySelectorPresenter`: nao tem endereco, recebe o `update` do
 * hospedeiro e devolve a escolha por um retorno de chamada. O PARAMETRO continua
 * sendo de quem hospeda - cada tela guarda o periodo no endereco dela, com o codigo
 * dela, e este pedaco de tela nao sabe que URL existe.
 *
 * ## O que ele decide, e o que nao decide
 *
 * DECIDE: como cada atalho vira intervalo, o que acontece quando alguem digita uma
 * data (vira `custom`), e o corte pelo teto.
 *
 * NAO DECIDE: quais atalhos existem nesta tela nem qual e o teto - os dois vem de
 * quem monta. E o que deixa o Relatorio de Sono oferecer cinco atalhos curtos com
 * teto de cinco semanas, e a Adocao e Engajamento oferecer os longos sem teto nenhum,
 * sobre o mesmo componente.
 */
export class PeriodSelectorPresenter {
    /**
     * Nao ha `this.update()` depois de escrever no scope, de proposito: o campo
     * `@observe()` ja avisa. O `update` fica guardado para o CONSTRUTOR entrega-lo ao
     * scope - e e o do hospedeiro, entao este pedaco de tela anda no mesmo ciclo de
     * atualizacao dele.
     *
     * @param update o `update` do presenter hospedeiro
     * @param shortcuts quais atalhos esta tela oferece, na ordem em que aparecem
     * @param onChange chamado quando o periodo muda; o hospedeiro escreve o endereco
     * @param maxDays o teto desta tela, quando ha um
     * @param remember se a escolha feita aqui vira o padrao dos outros seletores que
     *                 lembram (B-35). So os de periodo de DADO lembram; um filtro de
     *                 lista que abre em "todo o periodo" filtraria sem a pessoa pedir.
     */
    constructor(update, shortcuts, onChange, maxDays, remember = false) {
        this.onChange = onChange;
        this.maxDays = maxDays;
        this.remember = remember;
        this.scope = new PeriodScope();
        this.scope.update = update;
        this.scope.maxDays = maxDays ?? 0;
        this.scope.onFromChanged = this.onEdge.bind(this, 'from');
        this.scope.onToChanged = this.onEdge.bind(this, 'to');
        this.scope.options = shortcuts.map((shortcut) => {
            const option = new PeriodOptionScope();
            option.identity = shortcut;
            option.label = PERIOD_LABELS[shortcut];
            option.shortLabel = PERIOD_SHORT_LABELS[shortcut];
            option.value = shortcut;
            option.onSelect = async () => {
                await this.choose(shortcut);
            };
            return option;
        });
        LOG.debug('Initialized');
    }
    /**
     * O que o hospedeiro chama quando aplica os parametros do endereco.
     *
     * `from`/`to` so sao lidos quando o atalho e `custom` - nos outros o intervalo e
     * DERIVADO, e aceitar datas junto abriria a porta para um endereco em que o
     * atalho diz uma coisa e as datas dizem outra.
     */
    show(shortcut, from, to) {
        /*
         * UMA ponta basta para o intervalo ser o que veio.
         *
         * Era `from && to`, e um recorte aberto - "ate 11/09", sem inicio - caia no `else` e
         * virava o intervalo PADRAO de `custom`, que e a ultima semana: a tela mostrava
         * 17/09 a 23/09 sobre uma lista filtrada por outra coisa. Os cartoes de "ja fez", em
         * Adocao e Engajamento, mandam exatamente esse formato.
         */
        const range = shortcut === 'custom' && (from || to)
            ? clampRange(from ?? '', to ?? '', this.maxDays)
            : { ...periodRange(shortcut), clamped: false };
        this.scope.from = range.from;
        this.scope.to = range.to;
        this.scope.clamped = range.clamped;
        /*
         * Recorte ha quando ha UMA ponta que seja - ver `PeriodScope.applied`.
         *
         * Era `from && to`, e escondia o recorte aberto: "ate 11/09", que e o que os cartoes
         * de "ja fez" mandam, desenhava o seletor apagado e escrito "Todo o periodo" com a
         * lista ja filtrada. So "Todo o periodo" devolve as duas vazias.
         */
        this.scope.applied = !!(range.from || range.to);
        // "Todo o periodo" nao tem pontas para escrever - o rotulo dele ja diz tudo.
        this.scope.rangeLabel =
            range.from && range.to ? `${formatDate(range.from)} a ${formatDate(range.to)}` : PERIOD_LABELS[shortcut];
        this.scope.summary = PERIOD_LABELS[shortcut];
        for (const option of this.scope.options) {
            option.current = option.value === shortcut;
        }
    }
    /** O intervalo em vigor, para quem hospeda consultar o servidor. */
    get range() {
        return { from: this.scope.from, to: this.scope.to };
    }
    async choose(shortcut) {
        const range = shortcut === 'custom'
            ? // Escolher "Escolher datas" PRESERVA o que esta na tela: o leitor
                // quer ajustar o intervalo que esta vendo, e nao recomecar de um
                // intervalo qualquer.
                clampRange(this.scope.from, this.scope.to, this.maxDays)
            : { ...periodRange(shortcut), clamped: false };
        if (this.remember) {
            rememberPeriod({ shortcut, from: range.from, to: range.to });
        }
        await this.onChange({ from: range.from, to: range.to, shortcut });
    }
    /**
     * Uma das pontas foi digitada.
     *
     * Mexer numa data implica `custom`: o leitor acabou de dizer o que quer, e manter
     * o atalho ligado faria a proxima leitura do endereco jogar fora o que ele
     * digitou.
     *
     * Enquanto a data esta pela metade (`2026-09-` no meio da digitacao) o campo e
     * escrito e mais nada acontece - so um dia completo vale uma consulta.
     *
     * O que sobe e o intervalo CRU, sem corte. Quem corta e o `show`, na volta - e
     * essa ordem importa: cortando aqui, o endereco ja chegaria cortado, `show` nao
     * teria o que cortar, e o aviso de encurtamento nunca apareceria. Foi exatamente
     * isso que aconteceu: o periodo era encurtado corretamente e a tela nao dizia
     * por que.
     *
     * De quebra, o endereco passa a guardar o que a pessoa PEDIU, e nao o que coube.
     */
    onEdge(edge, value) {
        this.scope[edge] = value;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            return;
        }
        const from = edge === 'from' ? value : this.scope.from;
        const to = edge === 'to' ? value : this.scope.to;
        if (!from || !to || from > to) {
            return;
        }
        if (this.remember) {
            rememberPeriod({ shortcut: 'custom', from, to });
        }
        void this.onChange({ from, to, shortcut: 'custom' });
    }
}
//# sourceMappingURL=period.presenter.js.map